import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * The provider-signature flow for letters (PD-1407).
 *
 * A care coordinator or medical assistant prepares a clinical letter and asks a
 * provider to sign it. The provider reviews and signs, at which point the letter is
 * filed exactly as if they had written it themselves. Nothing reaches the patient
 * chart until a provider has signed, so an unapproved draft can never be mistaken
 * for a medical statement the clinic stands behind.
 *
 * There is no "send it back with a comment" step: a provider who wants something
 * changed can edit the letter, or ask the preparer and come back to it. Alex chose
 * the conversation over the workflow (2026-08-31).
 */

export const LETTER_REQUEST_STATUS_VALUES = ['awaiting_signature', 'completed'] as const

const letterDraftSchema = z.object({
  templateId: z.string(),
  title: z.string(),
  /** The template's field values, keyed by field name. */
  values: z.record(z.string(), z.string()),
  /** The reviewed letter text, exactly as it will print. */
  body: z.string(),
  /** The patient notification the preparer drafted; omitted means don't email. */
  email: z.object({ subject: z.string(), body: z.string() }).optional(),
})

const letterRequestSchema = z.object({
  id: z.string(),
  status: z.enum(LETTER_REQUEST_STATUS_VALUES),
  patientId: z.string(),
  patientName: z.string(),
  draft: letterDraftSchema,
  requestedBy: z.string(),
  assignedTo: z.string(),
  requestedAt: z.string(),
  documentReferenceId: z.string().optional(),
  /**
   * Which side the caller is on: the provider who has to sign, or the person who
   * prepared it (who sees it read-only). Resolved server-side so the client never
   * has to work out identity from references.
   */
  viewerRole: z.enum(['signer', 'preparer']),
})

// Ask a provider to sign a prepared letter. `assigneeId` is the provider who has
// to act; the preparer picks them, so nothing depends on an auto-assign rule.
export const letterSignatureRequestCreate = oc
  .route({ method: 'POST', path: '/v2/patients/{patientId}/letter-signature-requests' })
  .input(
    z.object({
      patientId: z.string(),
      assigneeId: z.string(),
      draft: letterDraftSchema,
    }),
  )
  .output(z.object({ requestId: z.string(), notified: z.boolean(), emailed: z.boolean() }))

// The caller's own queue: `toSign` are waiting on them, `raised` are the ones they
// prepared (so a coordinator can watch what is still unsigned).
export const letterSignatureRequestList = oc
  .route({ method: 'GET', path: '/v2/letter-signature-requests' })
  .input(z.object({}).optional())
  .output(
    z.object({
      toSign: z.array(letterRequestSchema),
      raised: z.array(letterRequestSchema),
    }),
  )

export const letterSignatureRequestGet = oc
  .route({ method: 'GET', path: '/v2/letter-signature-requests/{id}' })
  .input(z.object({ id: z.string() }))
  .output(letterRequestSchema)

// Approve & sign. Carries the PDF the signing provider just rendered — the
// signature on it is theirs, applied by them.
export const letterSignatureRequestApprove = oc
  .route({ method: 'POST', path: '/v2/letter-signature-requests/{id}/approve' })
  .input(
    z.object({
      id: z.string(),
      /** The signed PDF. */
      pdfBase64: z.string(),
      fileName: z.string(),
    }),
  )
  .output(
    z.object({
      status: z.enum(LETTER_REQUEST_STATUS_VALUES),
      documentReferenceId: z.string().optional(),
      emailSent: z.boolean().optional(),
      emailSkipReason: z.string().optional(),
    }),
  )

// Providers in the caller's organization who can be asked to sign. Care
// coordinators are excluded — they cannot sign a clinical letter, so offering them
// would only produce a request the server refuses.
export const letterSigners = oc
  .route({ method: 'GET', path: '/v2/letter-signers' })
  .input(z.object({}).optional())
  .output(z.object({ signers: z.array(z.object({ id: z.string(), name: z.string() })) }))
